import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Calendar, Award, Users, BookOpen, Globe } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';

const ProgrammesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-indigo-900 to-blue-700 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Study With Us
            </h1>
            <p className="text-2xl md:text-3xl mb-4 text-indigo-100">
              Programmes & Learning Opportunities
            </p>
            <p className="text-xl text-indigo-50 leading-relaxed">
              Rigorous training in field research, interdisciplinary methods, and community-engaged scholarship
            </p>
          </div>
        </div>
      </section>

      {/* Summer School */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Summer School</h2>
              <p className="text-xl text-gray-600">Launching the Research Journey</p>
              <div className="h-1 w-24 bg-indigo-600 mt-4"></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
              <div className="lg:col-span-2">
                <div className="prose prose-lg max-w-none">
                  <p className="text-gray-700 leading-relaxed mb-6">
                    The <strong>Summer School</strong> is the foundational phase of the PGCertR programme, marking the 
                    formal entry point for all postgraduate researchers at the Highland Institute's School of Advanced Studies. 
                    Held annually in June, this immersive, residential training module brings together selected early-career 
                    scholars for an intensive period of methodological instruction, research design, and academic mentorship.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Over several weeks, participants engage in rigorous and interdisciplinary training designed to prepare 
                    them for fieldwork, thesis development, and publication.
                  </p>
                </div>
              </div>
              <div className="bg-indigo-50 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 text-indigo-600 mr-2" />
                  Key Details
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-600 mt-1 mr-2 flex-shrink-0" />
                    <span><strong>When:</strong> Annually in June</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-600 mt-1 mr-2 flex-shrink-0" />
                    <span><strong>Where:</strong> Kohima, Nagaland</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-600 mt-1 mr-2 flex-shrink-0" />
                    <span><strong>Duration:</strong> 3 weeks residential + online</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-indigo-600 mt-1 mr-2 flex-shrink-0" />
                    <span><strong>Applications:</strong> Open in February</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* What to Expect */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What to Expect</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BookOpen className="w-5 h-5 text-indigo-600 mr-2" />
                      Advanced Research Methods
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      Training in anthropology, history, philosophy, environmental humanities, and interdisciplinary social sciences
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Users className="w-5 h-5 text-indigo-600 mr-2" />
                      Mentorship Sessions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      One-on-one guidance from Highland Fellows and visiting faculty members
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <GraduationCap className="w-5 h-5 text-indigo-600 mr-2" />
                      Research Ethics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      Decolonial approaches, community consent, and navigating field politics
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Globe className="w-5 h-5 text-indigo-600 mr-2" />
                      Indigenous Knowledge Dialogues
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">
                      Sessions with indigenous researchers, field practitioners, and local knowledge holders
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Certification */}
            <div className="bg-gray-50 p-8 rounded-lg mb-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Award className="w-6 h-6 text-indigo-600 mr-2" />
                Certification and Progression
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
                  <span>
                    Participants receive an <strong>Internship Certificate in Research Training</strong> upon completion
                  </span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
                  <span>
                    Option to continue into the full 12-month <strong>PGCertR programme</strong> with approval
                  </span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
                  <span>
                    Progression based on proposal quality, participation, and readiness for fieldwork
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PGCertR */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Postgraduate Certificate in Research (PGCertR)</h2>
              <p className="text-xl text-gray-600">12-Month Intensive Research Programme</p>
              <div className="h-1 w-24 bg-indigo-600 mt-4"></div>
            </div>

            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-gray-700 leading-relaxed">
                The PGCertR is an advanced research qualification for scholars committed to long-term, field-based 
                research in highland communities. Following the Summer School, selected participants embark on a 
                year-long journey of original research, writing, and publication.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <CardTitle>Original Research</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Conduct independent fieldwork under expert supervision, contributing new knowledge to your field
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <CardTitle>Publication Support</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Guidance on academic writing, peer review, and dissemination through Highland Press journals
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <CardTitle>Research Network</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Become part of a vibrant community of highland scholars and international collaborators
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Fellowship Programme */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Fellowship Programme</h2>
              <p className="text-xl text-gray-600">International Research Exchanges</p>
              <div className="h-1 w-24 bg-indigo-600 mt-4"></div>
            </div>

            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-gray-700 leading-relaxed">
                Through partnerships with institutions in Norway, Brazil, UK, and beyond, the Highland Institute 
                offers fellowship opportunities enabling scholars to engage in comparative research, collaborative 
                projects, and institutional exchanges.
              </p>
            </div>

            <div className="bg-indigo-50 p-8 rounded-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Ekologos Fellowship</h3>
              <p className="text-gray-700 mb-4">
                As part of the Ekologos project, selected participants can access:
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
                  <span>Study opportunities at UiT The Arctic University of Norway</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
                  <span>Research exchanges at UNICAMP, Brazil</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-indigo-600 mt-1 mr-3 flex-shrink-0" />
                  <span>Collaborative fieldwork across Arctic, Amazonian, and Himalayan regions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Winter School */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Winter School</h2>
              <p className="text-xl text-gray-600">Intensive Workshops & Collaborative Writing</p>
              <div className="h-1 w-24 bg-indigo-600 mt-4"></div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              The Winter School brings together researchers, practitioners, and scholars for intensive workshops, 
              collaborative writing projects, and thematic seminars. Hosted periodically in Nagaland as part of 
              international partnerships.
            </p>
          </div>
        </div>
      </section>

      {/* Research Themes */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Core Research Themes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Societies & Cultures of Highland Asia</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Oral traditions, kinship, religious change, customary law, and ancestral knowledge
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Environmental Knowledge & Climate Resilience</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Indigenous ecological knowledge, traditional land management, biodiversity conservation
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Religion, Healing & Medical Pluralism</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Shamanism, prophetic healing, medical pluralism, and rural healthcare ecologies
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Political Economy, Borders & Mobility</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Indigenous sovereignty, cross-border trade, migration, and spatial politics of belonging
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Visual, Material & Museum Anthropology</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">
                    Collaborative exhibitions, repatriation, ethnographic filmmaking, and creative humanities
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-16 bg-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">How to Apply</h2>
            <p className="text-xl text-gray-700 mb-8">
              Applications for Summer School open in <strong>February</strong> each year. The selection process 
              is competitive and prioritises alignment with the Institute's research themes and commitment to 
              community-rooted scholarship.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="mailto:info@highlandinstitute.org">
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg">
                  Request Application Updates
                </Button>
              </a>
              <Link to="/about">
                <Button variant="outline" className="border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-8 py-6 text-lg">
                  Learn More About Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Begin Your Research Journey?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join a community of scholars committed to locally rooted, globally engaged research
          </p>
          <a href="mailto:info@highlandinstitute.org">
            <Button className="bg-white text-indigo-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Contact Admissions
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProgrammesPage;